import React, { FC, useCallback, useEffect, useState } from 'react'
import { Spacer } from 'components/Utility'
import { Checkbox, SearchForm, SelectBox, TagCheckbox } from 'components/Form'
import { SmallHeadline } from 'components/Typography'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { getBreads, getIsShownPrivate } from 'reducks/bread/selectors'
import { getCategories } from 'reducks/category/selectors'
import { getStoreRef } from 'reducks/auth/selectors'
import { BreadType } from 'types/bread'
import { useCategoryOptions } from 'hooks/category'
import { fetchBreads, initBreads, toggleShownPrivateBreads } from 'reducks/bread/operations'
import { initSchedules } from 'reducks/schedule/operations'
import { fetchEnabledCategories } from 'reducks/category/operations'
import { FlexRow } from 'components/Flex'
import { BlueButton } from 'components/Button'
import { push } from 'connected-react-router'
import { CautionCard, BreadCardList } from 'components/Card'
import { fetchFutureStocks, initStocks } from 'reducks/stock/operations'
import { useBreadTypeFilter, useHandleKeyword, useInitBreadList, useSearchBreads, useSelectCategory } from 'hooks/bread'
import { getStocks } from 'reducks/stock/selectors'
import { initStoreDeliveryBread } from 'reducks/storeDeliveryBread/operations'
import { initStoreDeliveryFees } from 'reducks/storeDeliveryFee/operations'
import { FilterLabel } from 'components/Order'
import { BREAD_TYPES } from 'constants/bread'

const ProductContainer: FC = () => {
  const dispatch = useDispatch()

  const breads = useSelector((state: State) => getBreads(state))
  const categories = useSelector((state: State) => getCategories(state))
  const stocks = useSelector((state: State) => getStocks(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const isShownPrivate = useSelector((state: State) => getIsShownPrivate(state))
  const sortKey = 'order'

  const [categoryName, setCategoryName] = useState<string>('')
  const [keyword, setKeyword] = useState<string>('')
  const { breadTypes, handleBreadTypes, isAllBreadTypesSelected, selectAllBreadTypes } = useBreadTypeFilter()
  const [breadList, setBreadList] = useState<BreadType[]>([])

  const goToCreatePage = useCallback(() => {
    dispatch(initBreads())
    dispatch(initSchedules())
    dispatch(initStocks())
    dispatch(initStoreDeliveryFees())
    dispatch(push('/product/create'))
  }, [dispatch])

  // 選択されたカテゴリーに応じてフィルタリング
  const selectCategoryName = useSelectCategory({
    breads,
    categories,
    keyword,
    setBreadList,
    setCategoryName,
    isShownPrivate,
    breadTypes,
  })
  // 検索キーワードの入力
  const handleKeyword = useHandleKeyword({
    breads,
    categories,
    categoryName,
    setBreadList,
    setKeyword,
    isShownPrivate,
    breadTypes,
  })
  // キーワード検索(部分一致)
  const searchBreads = useSearchBreads({ breads, categories, categoryName, setBreadList, isShownPrivate, breadTypes })
  // Reduxで取得したstocksをローカルのbreadListに反映する
  useInitBreadList({ breads, categories, categoryName, keyword, setBreadList, isShownPrivate, breadTypes })

  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchBreads(storeRef, 'all', sortKey))
  }, [storeRef, sortKey])

  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchFutureStocks({ storeRef, types: ['preorder'] }))
  }, [storeRef])

  // カテゴリーを取得
  useEffect(() => {
    dispatch(fetchEnabledCategories())
  }, [])

  // 取り寄せ情報の初期化
  useEffect(() => {
    dispatch(initStoreDeliveryBread())
  }, [])

  // カテゴリの選択肢を生成
  const categoryOptions = useCategoryOptions(categories)

  return (
    <section>
      <Spacer size={20} />
      <SearchForm
        isFilled={keyword !== ''}
        onChange={handleKeyword}
        onClick={() => searchBreads(keyword)}
        placeholder={'商品名で検索する'}
        value={keyword}
      />
      <Spacer size={12} />
      <SelectBox options={categoryOptions} select={selectCategoryName} value={categoryName} />
      <Spacer size={12} />
      <FilterLabel label={'注文タイプ：'} />
      <Spacer size={8} />
      <FlexRow justifyContent={'start'} wrap={'wrap'}>
        {BREAD_TYPES.map((BREAD_TYPE) => {
          const { id, name } = BREAD_TYPE
          return (
            <TagCheckbox
              key={id}
              hasHashtag={false}
              id={id}
              isChecked={!isAllBreadTypesSelected && breadTypes.some((type) => type === id)}
              onChange={handleBreadTypes}
              type={'outlined'}
              value={name}
            />
          )
        })}
        <TagCheckbox
          hasHashtag={false}
          id={'all'}
          isChecked={isAllBreadTypesSelected}
          onChange={selectAllBreadTypes}
          type={'outlined'}
          value={'すべて'}
        />
      </FlexRow>
      <Spacer size={12} />
      <Checkbox
        id={'isShownPrivate'}
        isChecked={isShownPrivate}
        label={'販売停止中のパンも表示する'}
        handleCheckbox={() => dispatch(toggleShownPrivateBreads())}
      />
      <Spacer size={28} />
      <FlexRow justifyContent={'between'}>
        <SmallHeadline label={`${breadList.length}件の商品を表示中`} />
        <BlueButton
          label={'新しく登録する'}
          onClick={goToCreatePage}
          size={'xsmall'}
          startIconPath={'/icon/white/add.svg'}
          type={'main'}
        />
      </FlexRow>
      <Spacer size={8} />
      {breadList.length === 0 ? (
        <CautionCard
          align={'center'}
          caution={'該当商品がありません'}
          description={'別の方法で検索してみてください。'}
        />
      ) : (
        <BreadCardList breads={breadList} categories={categories} stocks={stocks} />
      )}
      <Spacer size={8} />
      <FlexRow justifyContent={'end'}>
        <BlueButton
          label={'新しく登録する'}
          onClick={goToCreatePage}
          size={'xsmall'}
          startIconPath={'/icon/white/add.svg'}
          type={'main'}
        />
      </FlexRow>
    </section>
  )
}

export default ProductContainer
